import type {
  CoreAdminProps,
  AuthProvider,
  DashboardComponent,
  LayoutComponent,
} from "ra-core";
import { CustomRoutes, localStorageStore, Resource } from "ra-core";
import { Navigate } from "react-router";
import { useEffect, useMemo, type ReactNode } from "react";
import { Route } from "react-router";
import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import { Admin } from "@/components/admin/admin";
import { ForgotPasswordPage } from "@/components/supabase/forgot-password-page";
import { SetPasswordPage } from "@/components/supabase/set-password-page";
import { OAuthConsentPage } from "@/components/supabase/oauth-consent-page";

import companies from "../companies";
import contacts from "../contacts";
import { Dashboard } from "../dashboard/Dashboard";
import { MobileDashboard } from "../dashboard/MobileDashboard";
import deals from "../deals";
import { Layout } from "../layout/Layout";
import { MobileLayout } from "../layout/MobileLayout";
import { SignupPage } from "../login/SignupPage";
import { ConfirmationRequired } from "../login/ConfirmationRequired";
import { ImportPage } from "../misc/ImportPage";
import { ChangelogPage } from "../misc/ChangelogPage";
import {
  getAuthProvider as defaultAuthProviderBuilder,
  getDataProvider as defaultDataProviderBuilder,
} from "../providers/supabase";
import sales from "../sales";
import { SettingsPageMobile } from "../settings/SettingsPageMobile";
import { ProfilePage } from "../settings/ProfilePage";
import { IntegrationsPage } from "../integrations/IntegrationsPage";
import { MarketingPage } from "../marketing/MarketingPage";
import { ReportsPage } from "../reports/ReportsPage";
import { TasksPage } from "../tasks/TasksPage";
import { InboxPage } from "../inbox/InboxPage";
import { SettingsPage } from "../settings/SettingsPage";
import { SettingsLayout } from "../settings/SettingsLayout";
import { SignalsSettings } from "../settings/SignalsSettings";
import { PrivacyPage } from "../settings/PrivacyPage";
import { EmailCapturePage } from "../settings/EmailCapturePage";
import { TrackingPage } from "../settings/TrackingPage";
import { BuildStamp } from "./BuildStamp";
import { HelpCenter } from "../help/HelpCenter";
import { HelpArticle } from "../help/HelpArticle";
import {
  CONFIGURATION_STORE_KEY,
  type ConfigurationContextValue,
} from "./ConfigurationContext";
import type { CrmDataProvider } from "../providers/types";
import {
  defaultCompanySectors,
  defaultCurrency,
  defaultDarkModeLogo,
  defaultDealCategories,
  defaultDealPipelineStatuses,
  defaultCustomFields,
  defaultDealStages,
  defaultCampaigns,
  defaultContactLists,
  defaultCompanySidebarModules,
  defaultDashboards,
  defaultCloseReasons,
  defaultCompanyProfile,
  defaultPlanLimits,
  defaultLifecycleStages,
  defaultScoringRules,
  defaultTimezone,
  defaultWorkflows,
  defaultSignals,
  defaultIntegrations,
  defaultEmailTemplates,
  defaultForms,
  defaultMailingAddress,
  defaultMarketingFromEmail,
  defaultMarketingFromName,
  defaultMarketingTrackEngagement,
  defaultMarketingApprovedDomain,
  defaultSequences,
  defaultTurnstileSiteKey,
  defaultLightModeLogo,
  defaultNoteStatuses,
  defaultTaskTypes,
  defaultTitle,
} from "./defaultConfiguration";
import { i18nProvider as defaulti18nProvider } from "../providers/commons/i18nProvider";
import { StartPage } from "../login/StartPage.tsx";
import { useIsMobile } from "@/hooks/use-mobile.ts";
import { MobileTasksList } from "../tasks/MobileTasksList.tsx";
import { ContactListMobile } from "../contacts/ContactList.tsx";
import { ContactShow } from "../contacts/ContactShow.tsx";
import { CompanyShow } from "../companies/CompanyShow.tsx";
import { NoteShowPage } from "../notes/NoteShowPage.tsx";

const defaultStore = localStorageStore(undefined, "CRM");

export type CRMProps = {
  dataProvider?: CrmDataProvider;
  authProvider?: AuthProvider;
  i18nProvider?: CoreAdminProps["i18nProvider"];
  disableTelemetry?: boolean;
  store?: CoreAdminProps["store"];
  dashboard?: DashboardComponent;
  layout?: LayoutComponent;
  /**
   * Operator-console routes (Grey Matter internal). ONLY the demo entry
   * passes this - tenant builds ship none of the console code or routes
   * (docs/decisions.md #13).
   */
  operatorRoutes?: ReactNode;
} & Partial<ConfigurationContextValue>;

/**
 * CRM Component
 *
 * This component sets up and renders the main CRM application using `ra-core`. It provides
 * default configurations and themes but allows for customization through props. The component
 * seeds the store with any custom prop values for backwards compatibility.
 *
 * @param {LabeledValue[]} companySectors - The list of company sectors used in the application.
 * @param {string} currency - The ISO 4217 currency code used to format monetary values (e.g. "USD", "EUR", "GBP").
 * @param {RaThemeOptions} darkTheme - The theme to use when the application is in dark mode.
 * @param {LabeledValue[]} dealCategories - The categories of deals used in the application.
 * @param {string[]} dealPipelineStatuses - The statuses of deals in the pipeline used in the application.
 * @param {DealStage[]} dealStages - The stages of deals used in the application.
 * @param {RaThemeOptions} lightTheme - The theme to use when the application is in light mode.
 * @param {string} darkModeLogo - Logo shown in dark mode and on the auth pages. Must be an imported asset, an absolute URL, or a data URI - never a route-relative path like "./logos/x.svg", which breaks on nested routes such as /oauth/consent (issue #291).
 * @param {string} lightModeLogo - Logo shown in light mode. Same rule as darkModeLogo: imported asset, absolute URL, or data URI only.
 * @param {NoteStatus[]} noteStatuses - The statuses of notes used in the application.
 * @param {LabeledValue[]} taskTypes - The types of tasks used in the application.
 * @param {string} title - The title of the CRM application.
 *
 * @returns {JSX.Element} The rendered CRM application.
 *
 * @example
 * // Basic usage of the CRM component
 * import { CRM } from '@/components/atomic-crm/dashboard/CRM';
 *
 * const App = () => (
 *     <CRM
 *         darkModeLogo="https://example.com/logo-dark.svg"
 *         lightModeLogo="https://example.com/logo-light.svg"
 *         title="My Custom CRM"
 *         lightTheme={{
 *             ...defaultTheme,
 *             palette: {
 *                 primary: { main: '#0000ff' },
 *             },
 *         }}
 *     />
 * );
 *
 * export default App;
 */
export const CRM = ({
  companySectors = defaultCompanySectors,
  currency = defaultCurrency,
  dealCategories = defaultDealCategories,
  dealPipelineStatuses = defaultDealPipelineStatuses,
  customFields = defaultCustomFields,
  forms = defaultForms,
  emailTemplates = defaultEmailTemplates,
  mailingAddress = defaultMailingAddress,
  marketingFromEmail = defaultMarketingFromEmail,
  marketingFromName = defaultMarketingFromName,
  marketingTrackEngagement = defaultMarketingTrackEngagement,
  marketingApprovedDomain = defaultMarketingApprovedDomain,
  turnstileSiteKey = defaultTurnstileSiteKey,
  contactLists = defaultContactLists,
  sequences = defaultSequences,
  campaigns = defaultCampaigns,
  dashboards = defaultDashboards,
  companySidebarModules = defaultCompanySidebarModules,
  scoringRules = defaultScoringRules,
  workflows = defaultWorkflows,
  signals = defaultSignals,
  integrations = defaultIntegrations,
  dealStages = defaultDealStages,
  darkModeLogo = defaultDarkModeLogo,
  lightModeLogo = defaultLightModeLogo,
  noteStatuses = defaultNoteStatuses,
  timezone = defaultTimezone,
  companyProfile = defaultCompanyProfile,
  planLimits = defaultPlanLimits,
  lifecycleStages = defaultLifecycleStages,
  closeReasons = defaultCloseReasons,
  taskTypes = defaultTaskTypes,
  title = defaultTitle,
  dataProvider = defaultDataProviderBuilder(),
  authProvider = defaultAuthProviderBuilder(),
  i18nProvider = defaulti18nProvider,
  store = defaultStore,
  disableTelemetry,
  operatorRoutes,
  ...rest
}: CRMProps) => {
  useEffect(() => {
    if (
      disableTelemetry ||
      process.env.NODE_ENV !== "production" ||
      typeof window === "undefined" ||
      typeof window.location === "undefined" ||
      typeof Image === "undefined"
    ) {
      return;
    }
    const img = new Image();
    img.src = `https://atomic-crm-telemetry.marmelab.com/atomic-crm-telemetry?domain=${window.location.hostname}`;
  }, [disableTelemetry]);

  // Seed the store with CRM prop values if not already stored
  // (backwards compatibility for prop-based config)
  useEffect(() => {
    if (!store.getItem(CONFIGURATION_STORE_KEY)) {
      store.setItem(CONFIGURATION_STORE_KEY, {
        companySectors,
        currency,
        dealCategories,
        dealPipelineStatuses,
        dealStages,
        customFields,
        forms,
        emailTemplates,
        mailingAddress,
        marketingFromEmail,
        marketingFromName,
        marketingTrackEngagement,
        marketingApprovedDomain,
        turnstileSiteKey,
        contactLists,
        sequences,
        campaigns,
        dashboards,
        companySidebarModules,
        scoringRules,
        workflows,
        signals,
        integrations,
        noteStatuses,
        timezone,
        companyProfile,
        planLimits,
        lifecycleStages,
        closeReasons,
        taskTypes,
        title,
        darkModeLogo,
        lightModeLogo,
      } satisfies ConfigurationContextValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store]);

  const isMobile = useIsMobile();

  // on login, pre-fetch the configuration to avoid a flickering
  // when accessing the app for the first time
  const wrappedAuthProvider = useMemo<AuthProvider>(
    () => ({
      ...authProvider,
      login: async (params: any) => {
        const result = await authProvider.login(params);
        try {
          const config = await dataProvider.getConfiguration();
          if (Object.keys(config).length > 0) {
            store.setItem(CONFIGURATION_STORE_KEY, config);
          }
        } catch {
          // Non-critical: config will load via useConfigurationLoader
        }
        return result;
      },
      handleCallback: async (params: any) => {
        if (!authProvider.handleCallback) {
          throw new Error(
            "handleCallback is not implemented in the authProvider",
          );
        }
        const result = await authProvider.handleCallback(params);
        try {
          const config = await dataProvider.getConfiguration();
          if (Object.keys(config).length > 0) {
            store.setItem(CONFIGURATION_STORE_KEY, config);
          }
        } catch {
          // Non-critical: config will load via useConfigurationLoader
        }
        return result;
      },
      logout: async (params: any) => {
        try {
          store.removeItem(CONFIGURATION_STORE_KEY);
        } catch {
          // Ignore
        }
        return authProvider.logout(params);
      },
    }),
    [authProvider, dataProvider, store],
  );

  const ResponsiveAdmin = isMobile ? MobileAdmin : DesktopAdmin;

  return (
    <>
      <ResponsiveAdmin
        dataProvider={dataProvider}
        authProvider={wrappedAuthProvider}
        i18nProvider={i18nProvider}
        store={store}
        loginPage={StartPage}
        requireAuth
        disableTelemetry
        operatorRoutes={operatorRoutes}
        {...rest}
      />
      <BuildStamp />
    </>
  );
};

const DesktopAdmin = (
  props: CoreAdminProps & {
    dashboard?: DashboardComponent;
    layout?: LayoutComponent;
    operatorRoutes?: ReactNode;
  },
) => {
  const { operatorRoutes, ...adminProps } = props;
  return (
    <Admin
      layout={adminProps.layout ?? Layout}
      dashboard={adminProps.dashboard ?? Dashboard}
      {...adminProps}
    >
      {operatorRoutes ? (
        <CustomRoutes noLayout>{operatorRoutes}</CustomRoutes>
      ) : null}
      <CustomRoutes noLayout>
        <Route path={SignupPage.path} element={<SignupPage />} />
        <Route
          path={ConfirmationRequired.path}
          element={<ConfirmationRequired />}
        />
        <Route path={SetPasswordPage.path} element={<SetPasswordPage />} />
        <Route
          path={ForgotPasswordPage.path}
          element={<ForgotPasswordPage />}
        />
        <Route path={OAuthConsentPage.path} element={<OAuthConsentPage />} />
      </CustomRoutes>

      <CustomRoutes>

        <Route path={TasksPage.path} element={<TasksPage />} />
        <Route path={InboxPage.path} element={<InboxPage />} />
        <Route path={ReportsPage.path} element={<ReportsPage />} />
        <Route path="/reports/:tab" element={<ReportsPage />} />
        <Route path={MarketingPage.path} element={<MarketingPage />} />
        <Route path="/marketing/:tab" element={<MarketingPage />} />

        <Route path="/settings" element={<Navigate to="/settings/profile" replace />} />
        <Route
          path="/settings/profile"
          element={<SettingsLayout><ProfilePage /></SettingsLayout>}
        />
        <Route
          path="/settings/account-defaults"
          element={<SettingsLayout><SettingsPage section="account-defaults" /></SettingsLayout>}
        />
        <Route
          path="/settings/signals"
          element={<SettingsLayout><SignalsSettings /></SettingsLayout>}
        />
        <Route
          path="/settings/billing"
          element={<SettingsLayout><SettingsPage section="billing" /></SettingsLayout>}
        />
        <Route
          path="/settings/objects"
          element={<SettingsLayout><SettingsPage section="objects" /></SettingsLayout>}
        />
        <Route
          path="/settings/lifecycle"
          element={<SettingsLayout><SettingsPage section="lifecycle" /></SettingsLayout>}
        />
        <Route
          path="/settings/custom-fields"
          element={<SettingsLayout><SettingsPage section="custom-fields" /></SettingsLayout>}
        />
        <Route
          path="/settings/integrations"
          element={<SettingsLayout><IntegrationsPage /></SettingsLayout>}
        />
        <Route
          path="/settings/import-export"
          element={<SettingsLayout><ImportPage /></SettingsLayout>}
        />
        <Route
          path="/settings/privacy"
          element={<SettingsLayout><PrivacyPage /></SettingsLayout>}
        />
        <Route
          path="/settings/email-capture"
          element={<SettingsLayout><EmailCapturePage /></SettingsLayout>}
        />
        <Route
          path="/settings/tracking"
          element={<SettingsLayout><TrackingPage /></SettingsLayout>}
        />
        {/* Legacy paths → hub */}
        <Route path={ProfilePage.path} element={<Navigate to="/settings/profile" replace />} />
        <Route path={IntegrationsPage.path} element={<Navigate to="/settings/integrations" replace />} />
        <Route path={ImportPage.path} element={<Navigate to="/settings/import-export" replace />} />

        <Route path={HelpCenter.path} element={<HelpCenter />} />
        <Route path="/help/:slug" element={<HelpArticle />} />

        <Route path={ChangelogPage.path} element={<ChangelogPage />} />
      </CustomRoutes>
      <Resource name="deals" {...deals} />
      <Resource name="contacts" {...contacts} />
      <Resource name="companies" {...companies} />
      <Resource name="contact_notes" />
      <Resource name="deal_notes" />
      <Resource name="tasks" />
      <Resource name="sales" {...sales} />
      <Resource name="tags" />
      <Resource name="notifications" />
    </Admin>
  );
};

const MobileAdmin = (
  props: CoreAdminProps & {
    dashboard?: DashboardComponent;
    layout?: LayoutComponent;
    operatorRoutes?: ReactNode;
  },
) => {
  const { operatorRoutes, ...adminProps } = props;
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        gcTime: 1000 * 60 * 60 * 24, // 24 hours
        networkMode: "offlineFirst",
      },
      mutations: {
        networkMode: "offlineFirst",
      },
    },
  });
  const asyncStoragePersister = createAsyncStoragePersister({
    storage: localStorage,
  });

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{ persister: asyncStoragePersister }}
    >
      <Admin
        queryClient={queryClient}
        layout={adminProps.layout ?? MobileLayout}
        dashboard={adminProps.dashboard ?? MobileDashboard}
        {...adminProps}
      >
        {operatorRoutes ? (
          <CustomRoutes noLayout>{operatorRoutes}</CustomRoutes>
        ) : null}
        <CustomRoutes noLayout>
          <Route path={SignupPage.path} element={<SignupPage />} />
          <Route
            path={ConfirmationRequired.path}
            element={<ConfirmationRequired />}
          />
          <Route path={SetPasswordPage.path} element={<SetPasswordPage />} />
          <Route
            path={ForgotPasswordPage.path}
            element={<ForgotPasswordPage />}
          />
          <Route path={OAuthConsentPage.path} element={<OAuthConsentPage />} />
        </CustomRoutes>
        <CustomRoutes>
          <Route
            path={SettingsPageMobile.path}
            element={<SettingsPageMobile />}
          />
          <Route path={HelpCenter.path} element={<HelpCenter />} />
          <Route path="/help/:slug" element={<HelpArticle />} />
          <Route path={ChangelogPage.path} element={<ChangelogPage />} />
        </CustomRoutes>
        <Resource
          name="contacts"
          list={ContactListMobile}
          show={ContactShow}
          recordRepresentation={contacts.recordRepresentation}
        >
          <Route path=":id/notes/:noteId" element={<NoteShowPage />} />
        </Resource>
        <Resource name="companies" show={CompanyShow} />
        <Resource name="tasks" list={MobileTasksList} />
      </Admin>
    </PersistQueryClientProvider>
  );
};
